import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import {
  Box, Chip, CircularProgress, Divider,
  IconButton, Tooltip, Typography,
} from '@mui/material'
import { FancyButton } from '../comps/FancyButtons'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DownloadIcon from '@mui/icons-material/Download'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt'
import { useApp, useAppActions, useAppProject, useNav } from '../AppContext'
import { addToast } from '../services/Toasts'
import taapi from '../services/taapi'
import { useQuestionProcessing } from '../services/TaskManagerService'
import type { ReplyLimit } from '../types/question'
import type { Sentence, TextStructure } from '../types/textstructure'
import { FS_XL, FS_XS, ICON_XS, FS_SM } from '../common/theme'
import { useTempQuestion } from '../AppContext'
import { copyToClipboard } from '../common/clipboardHelpers'
import { downloadFile } from '../common/downloadHelpers'
import TaWordLimit, { WORD_LIMIT_PRESETS } from '../comps/TaWordLimit'
import TaWordCount from '../comps/TaWordCount'
import TaQueryBar from '../comps/TaQueryBar'
import TaResponse, { structureToPlainText } from '../comps/TaResponse'
import TabHeader from '../comps/TabHeader'
import TraceArc from '../comps/TraceArc'
import ScoreArc from '../comps/ScoreArc'

type ViewTab = 'text' | 'trace'

// One round of the wizard. `modstructure` carries both columns' state
// (the original text, the modification candidate, ischecked toggles,
// per-sentence edits, deletions) as a TextStructure so we can hand it
// straight to TaResponse. `modsentencesmeta` holds the server-side
// summary fields (word counts, source-match) that aren't per-sentence.
interface Iteration {
  inputtext: string
  modstructure: TextStructure | null
  // Frozen snapshot of the original-side projection at wwinit time.
  // Drives the read-only left column. Never mutated by user actions
  // so toggling / editing / deleting on the modified side can't
  // bleed into the original column's display.
  origstructure: TextStructure | null
  modsentencesmeta: {
    origwordcount?: number
    modwordcount?: number
    origsourcematch?: any
    modsourcematch?: any
  } | null
  // bideval result captured from runScore against the merged modified
  // output for this iteration. Cleared whenever `modstructure` is
  // mutated so a stale score can't sit next to text the user has
  // since edited / toggled / deleted.
  modbideval?: any
  // answerobj returned from runTrace against the merged modified
  // output. Carries the simint that drives the modified TraceArc.
  // Cleared on every structure mutation alongside modbideval.
  modtraceobj?: any
}

// Shared settings - these stay the same across every iteration in a
// single wizard session. `wwp` carries the originating-question
// metadata when the wizard was opened from BidQuestion / RBT.
interface WwSettings {
  replylimit: ReplyLimit
  referral: string
  wwp?: any
}

const DEFAULT_REPLY_LIMIT: ReplyLimit = {
  answerlimit: 500,
  charcount: false,
  approx: false,
  speciallengthstring: 'none',
}

// Per-character diff splicing helpers. The server emits each modified
// sentence as a single string carrying inline `<span class='spandel'>`
// (parts the modification would drop) and `<span class='spanadd'>`
// (parts it would add). To drive the wizard's two-column UI we need:
//   - markup            : the original variant + spandel highlights
//   - markupmod         : the modified variant + spanadd highlights
//   - originaltext      : plain-text of the original (no spans)
//   - modtext           : plain-text of the modification (no spans)
// Each helper strips one of the two span classes and either drops the
// matching span entirely or unwraps its inner text.

const SPAN_ADD_DROP = /<span\s+class=(['"])spanadd\1>[\s\S]*?<\/span>\s?/gi
const SPAN_DEL_DROP = /<span\s+class=(['"])spandel\1>[\s\S]*?<\/span>\s?/gi
const SPAN_ADD_UNWRAP = /<span\s+class=(['"])spanadd\1>([\s\S]*?)<\/span>/gi
const SPAN_DEL_UNWRAP = /<span\s+class=(['"])spandel\1>([\s\S]*?)<\/span>/gi

const sentenceHasDiff = (html: string): boolean =>
  /<span\s+class=['"]span(?:add|del)['"]/i.test(html ?? '')

const makeMarkupOriginal = (html: string): string =>
  (html ?? '').replace(SPAN_ADD_DROP, '').replace(/\s+/g, ' ').trim()

const makeMarkupModified = (html: string): string =>
  (html ?? '').replace(SPAN_DEL_DROP, '').replace(/\s+/g, ' ').trim()

const extractPlainOriginal = (html: string): string =>
  (html ?? '')
    .replace(SPAN_ADD_DROP, '')
    .replace(SPAN_DEL_UNWRAP, '$2')
    .replace(/\s+/g, ' ')
    .trim()

const extractPlainModified = (html: string): string =>
  (html ?? '')
    .replace(SPAN_DEL_DROP, '')
    .replace(SPAN_ADD_UNWRAP, '$2')
    .replace(/\s+/g, ' ')
    .trim()

// Lift the server's `modifiedanswerobj.answerstructure` into the
// wizard's working shape. The structure already mirrors the original
// question's paragraph/line layout, so we just walk it and enrich each
// sentence with the wizard-specific fields used by TaResponse
// (originaltext / modtext / markup / markupmod). Sentences whose text
// didn't change (no diff spans) skip the modification fields entirely
// so the per-sentence radio + delete affordances stay hidden for them.
function parseModifiedAnswerstructure(struct: TextStructure | undefined | null): TextStructure | null {
  if (!struct) return null
  const out: TextStructure = structuredClone(struct)
  let nextId = 0
  for (const para of out.paragraphs ?? []) {
    for (const line of para.lines ?? []) {
      for (const sent of line.sentences ?? []) {
        const sentAny = sent as Sentence
        const raw = sentAny.sentence ?? ''
        if (sentenceHasDiff(raw)) {
          const origText = extractPlainOriginal(raw)
          const modText = extractPlainModified(raw)
          sentAny.originaltext = origText
          sentAny.modtext = modText
          sentAny.markup = makeMarkupOriginal(raw)
          sentAny.markupmod = makeMarkupModified(raw)
          // Default to the modification - the server marks every
          // rewritten sentence with `ischecked=true`. Replace the diff
          // HTML in `sentence` with clean text so word counts and the
          // editable contenteditable see the right characters.
          sentAny.sentence = sentAny.ischecked ? modText : origText
        } else {
          sentAny.originaltext = raw
        }
        if (sentAny.deleted == null) sentAny.deleted = false
        // Stable id per sentence so React keys are deterministic
        // across iterations even when the server doesn't supply one.
        if (!sentAny.id) sentAny.id = `ww_${nextId}`
        nextId++
      }
    }
  }
  return out
}

// Walk modstructure and produce the final answerstructure to write
// back to the question on Apply. Drops deleted sentences, scrubs the
// wizard-only fields, and leaves the chosen-variant text already
// living in `sentence.sentence` from toggle / edit. The shape mirrors
// the original answerstructure so the question's other surfaces don't
// see any structural change.
function modStructureForApply(struct: TextStructure | null | undefined): TextStructure | null {
  if (!struct) return null
  return {
    paragraphs: (struct.paragraphs ?? []).map(p => ({
      lines: (p.lines ?? []).map(l => ({
        sentences: (l.sentences ?? [])
          .filter(s => !s.deleted)
          .map(s => {
            const { markup, markupmod, originaltext, modtext, deleted, ...rest } = s as any
            void markup; void markupmod; void originaltext; void modtext; void deleted
            return rest
          }),
      })),
    })),
  } as unknown as TextStructure
}

// Project the wizard structure so the original column's Text view
// shows the immutable original text (rather than the current edited /
// toggled text). Each sentence's `sentence` field is overwritten with
// `originaltext`, leaving the rest of the metadata intact.
function projectOriginalStructure(struct: TextStructure | null | undefined): TextStructure | null {
  if (!struct) return null
  return {
    paragraphs: (struct.paragraphs ?? []).map(p => ({
      lines: (p.lines ?? []).map(l => ({
        sentences: (l.sentences ?? []).map(s => ({
          ...s,
          sentence: s.originaltext ?? s.sentence ?? '',
        })),
      })),
    })),
  } as unknown as TextStructure
}

export default function WordWizardTab() {
  const { pendingWwInit } = useApp()
  const { currentProject } = useAppProject()
  const { setPendingWwInit, updateQuestion, buildTempQuestion } = useAppActions()
  const { setActiveTab } = useNav()

  const [wwView, setWwView] = useState({ isinputview: true, isprocessing: false })
  const [wwSettings, setWwSettings] = useState<WwSettings>({
    replylimit: DEFAULT_REPLY_LIMIT,
    referral: '',
  })
  const [iterations, setIterations] = useState<Iteration[]>([
    { inputtext: '', modstructure: null, origstructure: null, modsentencesmeta: null },
  ])
  const [currentIter, setCurrentIter] = useState(0)
  // One tab control shared by both columns - flipping the active tab
  // here re-renders both TaResponse instances in the matching view so
  // Original and Modified stay in lock-step.
  const [viewTab, setViewTab] = useState<ViewTab>('trace')

  // Both panes share the live question's processing flags now that
  // the modified arc's built-in GenerateButton also fires against the
  // live question's sha — a single in-flight run animates the spinner
  // on both sides. `modScoring` / `modTracing` are kept as aliases so
  // the existing arc props stay readable; they're declared lower down
  // where `origScoring` / `origTracing` come into scope.


  // Tracks whether the user has touched the word-limit picker themselves.
  // While false, pasting / typing into the editor auto-snaps the limit
  // down to the largest preset shorter than the current word (or char)
  // count, so "Reduce Word Count" has a meaningful target to hit. The
  // first manual change to the picker locks the limit to the user's
  // choice and disables the auto-adjust until the wizard is cleared.
  const userPickedLimitRef = useRef(false)

  const current: Iteration = iterations[currentIter] ?? { inputtext: '', modstructure: null, origstructure: null, modsentencesmeta: null }

  const isRbOrPm = wwSettings.referral === 'rb' || wwSettings.referral === 'pm'

  // ─── State helpers ────────────────────────────────────────────────────────

  const patchIteration = (index: number, patch: Partial<Iteration>) => {
    setIterations(prev => prev.map((p, i) => (i === index ? { ...p, ...patch } : p)))
  }

  // Reset everything back to the empty paste-mode state - called by
  // Cancel and after a successful Apply.
  const handleClear = () => {
    setWwSettings({ replylimit: DEFAULT_REPLY_LIMIT, referral: '' })
    setIterations([{ inputtext: '', modstructure: null, origstructure: null, modsentencesmeta: null }])
    setCurrentIter(0)
    setWwView({ isinputview: true, isprocessing: false })
    setViewTab('trace')
    userPickedLimitRef.current = false
  }

  // The user touched the word-limit picker - lock to their choice and
  // stop auto-adjusting from input-text changes.
  const handleReplylimitChange = (rl: ReplyLimit) => {
    userPickedLimitRef.current = true
    setWwSettings(prev => ({ ...prev, replylimit: rl }))
  }

  // Pick the largest preset that's strictly shorter than the current
  // word (or char) count of the pasted-in text and snap the wizard's
  // target answerlimit to it. Skipped once the user has manually picked
  // a limit (userPickedLimitRef) so we never overwrite their choice.
  const autoAdjustLimit = (html: string) => {
    if (userPickedLimitRef.current) return
    const plain = (html ?? '').replace(/<[^>]+>/g, ' ')
    if (!plain.trim()) return
    const isChars = !!wwSettings.replylimit.charcount
    const count = isChars ? plain.length : (plain.trim().match(/\S+/g) ?? []).length
    const valueOf = (p: typeof WORD_LIMIT_PRESETS[number]) => isChars ? p.chars : p.words
    const target = [...WORD_LIMIT_PRESETS]
      .filter(p => valueOf(p) < count)
      .sort((a, b) => valueOf(b) - valueOf(a))[0]
    if (!target) return
    const next = valueOf(target)
    if (next === wwSettings.replylimit.answerlimit) return
    setWwSettings(prev => ({ ...prev, replylimit: { ...prev.replylimit, answerlimit: next } }))
  }

  // ─── Refine flow ──────────────────────────────────────────────────────────

  // Run /api2/wwinit against a specific iteration's text and write
  // the result onto that iteration. The server now returns the
  // modification directly as `modifiedanswerobj.answerstructure` (a
  // clone of the input answerstructure with each rewritten sentence's
  // `sentence` field holding inline spandel + spanadd markup). We
  // enrich each sentence with the wizard-only fields TaResponse reads
  // off (originaltext / modtext / markup / markupmod), then use the
  // resulting structure verbatim as both the source of truth and the
  // base we'll write back to the question on Apply.
  const runRefineForIter = useCallback(async (iter: number, text: string, settings: WwSettings) => {
    if (!text.trim()) return
    setWwView({ isinputview: false, isprocessing: true })
    try {
      const result = await taapi.post('/api2/wwinit', {
        inputtext: text,
        replylimit: settings.replylimit,
        modsentences: null,
        referral: settings.referral,
        wwp: settings.wwp,
      })
      if (result?.success) {
        const modAnswer: any = result?.modifiedanswerobj ?? {}
        const origAnswer: any = settings.wwp?.answerobj ?? {}
        const modstructure = parseModifiedAnswerstructure(modAnswer?.answerstructure)
        const modsentencesmeta = {
          origwordcount: typeof origAnswer?.wordcount === 'number' ? origAnswer.wordcount : undefined,
          modwordcount: typeof modAnswer?.wordcount === 'number' ? modAnswer.wordcount : undefined,
          // The trace arcs read `.simint` off these shapes, so keep
          // the same field names the rest of the code already uses.
          origsourcematch: typeof origAnswer?.simint === 'number' ? { simint: origAnswer.simint } : undefined,
          modsourcematch: typeof modAnswer?.simint === 'number' ? { simint: modAnswer.simint } : undefined,
        }
        // Snapshot the original-side projection now - frozen for the
        // life of the iteration so user actions on `modstructure`
        // (toggle / edit / delete) can't bleed into the original
        // column's display.
        const origstructure = projectOriginalStructure(modstructure)
        setIterations(prev => prev.map((p, i) => (i === iter
          ? { ...p, modstructure, origstructure, modsentencesmeta }
          : p)))
        setWwView({ isinputview: false, isprocessing: false })
      } else {
        addToast('error', 'Word wizard failed.')
        setWwView({ isinputview: true, isprocessing: false })
      }
    } catch {
      addToast('error', 'Failed to process word wizard.')
      setWwView({ isinputview: true, isprocessing: false })
    }
  }, [])

  // Handle the incoming pendingWwInit from BidQuestion / RBT - seed a
  // fresh iteration list with the originating question's text, then
  // immediately refine it.
  useEffect(() => {
    if (!pendingWwInit) return
    const settings: WwSettings = {
      replylimit: pendingWwInit.replylimit ?? DEFAULT_REPLY_LIMIT,
      referral: pendingWwInit.referral ?? '',
      wwp: pendingWwInit.wwp,
    }
    const initText = pendingWwInit.inputtext ?? ''
    setWwSettings(settings)
    setIterations([{ inputtext: initText, modstructure: null, origstructure: null, modsentencesmeta: null }])
    setCurrentIter(0)
    setPendingWwInit(null)
    runRefineForIter(0, initText, settings)
  }, [pendingWwInit]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefine = () => runRefineForIter(currentIter, current.inputtext, wwSettings)

  // ─── Merge / output helpers ───────────────────────────────────────────────

  // Build the current iteration's output as a TextStructure. The
  // modstructure is the single source of truth - every user action
  // (toggle / edit / delete / type / paste / undo / redo) commits
  // through `handleModStructureChange`, so Apply just drops deleted
  // sentences and scrubs the wizard-only diff fields.
  const buildMergedStructure = (): TextStructure | null => modStructureForApply(current.modstructure)

  // Project the merged structure back to plain text - same shape as
  // TaResponse uses internally.
  const buildOutputText = (): string => {
    const merged = buildMergedStructure()
    return merged ? structureToPlainText(merged) : ''
  }

  const handleCopy = () => {
    const text = buildOutputText()
    if (!text) return
    copyToClipboard(text, {
      onSuccess: () => addToast('success', 'Copied to clipboard'),
      onError: () => addToast('error', 'Failed to copy to clipboard'),
    })
  }

  const handleDownload = () => {
    const text = buildOutputText()
    if (!text) return
    downloadFile({ data: text, filename: 'answer.txt' })
  }

  // ─── Iteration flow ───────────────────────────────────────────────────────

  // Reduce Again: bake current selections into a new iteration's
  // inputtext, append it to the iteration history, switch the active
  // tab to it, and refine immediately. The previous iteration is left
  // intact so the user can flick back to compare.
  const handleReduceAgain = () => {
    // Capture iteration N's merged output BEFORE we switch to the
    // new iteration. The merged structure (deleted sentences dropped,
    // wizard fields scrubbed) is the input that iteration N+1 should
    // start from.
    const merged = buildMergedStructure()
    const text = merged ? structureToPlainText(merged) : ''
    if (!merged || !text.trim()) return
    const newIndex = iterations.length
    setIterations(prev => [...prev, { inputtext: text, modstructure: null, origstructure: null, modsentencesmeta: null }])
    setCurrentIter(newIndex)
    // The server has two wwinit paths:
    //   • `referral=rb|pm` ⇒ WordWizardRBReferral, which reduces
    //     `wwp.answerobj.answerstructure` directly (preserving the
    //     paragraph / line / sentence shape).
    //   • `referral=''` ⇒ WordWizardPage, which extracts a fresh
    //     TextStructure from plain `inputtext` (re-paragraphs, drops
    //     line breaks that don't match its heuristic).
    //
    // Iteration N+1 ALWAYS wants the structure-preserving path so the
    // shape iteration N committed doesn't reflow. In question mode we
    // already have a `wwp` — just splice the merged structure in. In
    // pasted-text mode we synthesise a wwp with empty `sequenceids`
    // (no sources to score against in this mode) and flip the
    // referral to `rb` so the server picks the structure path.
    const isPastedMode = !wwSettings.wwp
    const baseWwp = wwSettings.wwp ?? { sequenceids: [] as string[] }
    const settingsForIter: WwSettings = {
      ...wwSettings,
      referral: isPastedMode ? 'rb' : wwSettings.referral,
      wwp: {
        ...baseWwp,
        answerobj: {
          ...((baseWwp as any).answerobj ?? {}),
          answerstructure: merged,
        },
      },
    }
    runRefineForIter(newIndex, text, settingsForIter)
  }

  // ─── Apply / Cancel ───────────────────────────────────────────────────────

  const handleApply = () => {
    if (!current.modstructure) return
    const targetSha = wwSettings.wwp?.questionsha
    if (!isRbOrPm || !targetSha) return
    const finalStructure = buildMergedStructure()
    if (!finalStructure) return
    const liveQ: any = currentProject?.questions?.find((q: any) => q.questionsha === targetSha)
    const baseAnswerobj = liveQ?.answerobj ?? wwSettings.wwp?.answerobj ?? {}
    // If the user scored the modified text in this iteration, carry
    // that bideval onto the question so the question's ScoreArc
    // reflects what they just applied. Otherwise leave whatever
    // bideval is already on the question alone (don't clear it).
    const finalBideval = current.modbideval
    updateQuestion(targetSha, {
      answerobj: { ...baseAnswerobj, answerstructure: finalStructure } as any,
      ...(finalBideval ? { bideval: finalBideval } : {}),
    })
    addToast('success', 'Applied to question.')
    const referral = wwSettings.referral
    handleClear()
    setActiveTab(referral === 'pm' ? 'projectstab' : 'response')
  }

  const handleCancel = () => handleClear()

  // ─── TaResponse onChange - write structure changes back to the iteration ──

  const handleModStructureChange = useCallback((next: TextStructure) => {
    // Single canonical commit path for every user action - toggle,
    // delete / restore, per-keystroke edits, paste, undo / redo. Wipes
    // every cached evaluation of the modified text whenever the
    // structure changes so a stale score / trace can never sit next to
    // the user's current text.
    setIterations(prev => prev.map((p, i) => (i === currentIter
      ? {
        ...p,
        modstructure: next,
        modbideval: null,
        modtraceobj: null,
        modsentencesmeta: p.modsentencesmeta
          ? { ...p.modsentencesmeta, modsourcematch: null }
          : p.modsentencesmeta,
      }
      : p)))
  }, [currentIter])

  // ─── Scoring / Tracing ─────────────────────────────────────────────────
  // The Original side fires runScore / runTrace against the live
  // question - mirrors GenerateScoringButton / GenerateTraceButton in
  // BidQuestion.tsx, including writing the result back through
  // updateQuestion so the question's own ScoreArc / TraceArc updates
  // in every other surface.
  //
  // Modified-side runs land on the live question via AppContext too —
  // the GenerateButton inside each arc fires runScore / runTrace
  // against the question's sha and the result flows back through
  // updateQuestion, same as the Original side. The iteration's
  // `modbideval` / `modtraceobj` fields are now updated only by the
  // initial wwinit response (overall source-match) and aren't
  // re-computed locally — the loading spinner shares `origScoring` /
  // `origTracing` since there's a single in-flight run per question.

  // The left (read-only) TaResponse still gets an `onChange` prop for
  // completeness, but never fires - `editable={false}` + (in Trace
  // view) `showOriginal` suppress every mutation affordance.
  const handleOrigStructureNoop = useCallback(() => { }, [])

  // Frozen original-side structure captured at wwinit time. The
  // original column reads off this so toggle / edit / delete on the
  // modified side can't propagate into what the user sees as "the
  // original answer".
  const originalStructure = current.origstructure

  // Score / trace state for the originating question (live, from
  // currentProject). Only meaningful in question mode (isRbOrPm +
  // questionsha present).
  const targetSha = wwSettings.wwp?.questionsha as string | undefined
  const question: any = targetSha
    ? currentProject?.questions?.find((q: any) => q.questionsha === targetSha)
    : null
  // runScore / runTrace flip these flags on the question's task-manager
  // entry - so reading them here drives the Original-side spinners for
  // free.
  const origProcessing = useQuestionProcessing(targetSha ?? '')
  const origScoring = !!origProcessing.isrunningscoring
  const origTracing = !!origProcessing.isrunningtrace
  // Aliased so the Modified-side arcs read off the same in-flight
  // flag — see the comment near the top of the component.
  const modScoring = origScoring
  const modTracing = origTracing

  const origSimint: number | undefined = typeof question?.answerobj?.simint === 'number'
    ? question.answerobj.simint
    : (typeof current.modsentencesmeta?.origsourcematch?.simint === 'number'
      ? current.modsentencesmeta.origsourcematch.simint
      : undefined)

  // Temp question that the Modified-side arcs hand to their
  // default score / trace modals. `buildTempQuestion` gives it
  // a `temp_<sha>` questionsha + answersha so it can't be mistaken
  // for the live record; the merged answerstructure goes in via the
  // patch, and the iteration's `modbideval` rides along (null clears
  // — "different answer ⇒ different bideval" when the user hasn't
  // rescored yet).
  const baseQuestion = question ?? wwSettings.wwp;
  const initialModSimint = current.modsentencesmeta?.modsourcematch?.simint

  // Memoise so we don't churn a fresh synth question (and a fresh
  // synth registry entry) on every render — the synth sha needs to
  // stay stable across renders so `useTempQuestion` can subscribe
  // to it and `useQuestionProcessing` inside the arc's Generate button
  // can track the run's spinner. Recomputes only when the underlying
  // modified answer / score changes. Seed `answerobj.simint` from the
  // wwinit source-match so the arc reads the initial trace before any
  // live re-trace fires.
  const modifiedQuestion = useMemo(() => {
    if (!current.modstructure) return null
    const answerstructure =
      modStructureForApply(current.modstructure) ??
      baseQuestion?.answerobj?.answerstructure
    const q = buildTempQuestion(baseQuestion ?? {}, {
      answerobj: { ...(baseQuestion?.answerobj ?? {}), answerstructure },
      bideval: current.modbideval ?? null,
    })
    if (q && typeof initialModSimint === 'number') {
      q.answerobj.simint = initialModSimint
    }
    return q
  }, [current.modstructure, current.modbideval, initialModSimint, baseQuestion])

  // Subscribe to the synth's version counter so `applyTempPatch`
  // (fired from `updateQuestion` when runTrace / runScore complete
  // against the synth's sha) triggers a re-render. The object
  // reference itself stays the same — patches mutate in place — so
  // reading `modifiedQuestion.bideval` / `modifiedQuestion.answerobj`
  // below sees the latest values on every version bump.
  // useTempQuestion(modifiedQuestion?.questionsha)

  // Arc values read straight off the synth so `applyTempPatch`
  // updates flow through — iteration-local `modbideval` / `modtraceobj`
  // are legacy fallbacks kept only for the initial wwinit response
  // (already folded into the synth above via `initialModSimint`).
  const modSimint: number | undefined = typeof modifiedQuestion?.answerobj?.simint === 'number'
    ? modifiedQuestion.answerobj.simint
    : undefined

  return (
    <Box data-testid="ww_root" sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>



      {/* ── Toolbar ─────────────────────────────────────────────────── */}
      <Box data-testid="ww_toolbar" sx={{ display: 'flex', flexShrink: 0, borderBottom: 1, borderColor: 'divider' }}>

        {/* Left: Original (or the previous iteration's modified output
            when the user is past the first refine). The left column
            always shows what the wizard *started* from for this
            iteration - for v1 that's the question's original answer,
            for vN>1 it's "Modified v(N-1)" (the structure that was
            fed back into the wizard for the next pass). */}
        <Box sx={{ flex: 1, borderRight: 1, borderColor: 'divider', px: 1, py: 0.75, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: FS_SM }}>
            {currentIter === 0 ? 'Original' : `Modified v${currentIter}`}
          </Typography>
          {/* Right-justify the trace + score arcs. */}
          <Box sx={{ flex: 1 }} />
          {/* Trace + Score arcs. Same pattern as TaAdvEditModal — the
              arc owns all three states: a real value renders the arc,
              `loading` swaps the centre for a spinner, and passing
              `question` makes the N/A state render a Generate* button
              in the centre. In pasted-text mode there's no live
              question to score / trace against, so we render the arc
              only when a value is present and omit `question` (the
              naSlot Generate button is suppressed). */}
          {isRbOrPm && targetSha ? (
            <TraceArc
              loading={origTracing}
              tooltip="Original trace similarity"
              question={question}
              modalTitle="Trace Similarity - Original"
            />
          ) : (
            origSimint !== undefined && (
              <TraceArc
                value={origSimint}
                tooltip="Original trace similarity"
                structure={current.origstructure ?? undefined}
              />
            )
          )}
          {isRbOrPm && targetSha && (
            <ScoreArc
              loading={origScoring}
              tooltip="Original estimated score"
              question={question}
              modalTitle="Estimated Score - Original"
            />
          )}
        </Box>

        {/* Middle: word limit + word counts (wizard trigger lives on the
            footer Reduce Again button now). */}
        <Box sx={{ flexShrink: 0, borderRight: 1, borderColor: 'divider', px: 1.25, py: 0.75, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          <TaWordLimit
            replylimit={wwSettings.replylimit}
            onChange={handleReplylimitChange}
          />
          {current.modstructure && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TaWordCount structure={current.origstructure ?? undefined} replylimit={wwSettings.replylimit} />
              <ArrowRightAltIcon sx={{ fontSize: FS_XL, opacity: 0.5 }} />
              <TaWordCount structure={current.modstructure ?? undefined} replylimit={wwSettings.replylimit} />
            </Box>
          )}
        </Box>

        {/* Right: Modified vN - N matches the current iteration number
            so it lines up with the iteration chip selected in the
            TabHeader's endSlot below. */}
        <Box sx={{ flex: 1, px: 1, py: 0.75, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5, gap: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, fontSize: FS_SM }}>{`Modified v${currentIter + 1}`}</Typography>
            {/* Right-justify everything from here on so trace + score
                arcs sit alongside the action icons on the far right. */}
            <Box sx={{ flex: 1 }} />
            {/* Modified Trace + Score. Same single-arc pattern as the
                original side: `loading` for the in-flight spinner and
                `question` for the N/A naSlot Generate button. In
                pasted-text mode there's no live question to recompute
                against, so we only render the arc when a value is
                already present (no Generate button). */}
            {current.modstructure && isRbOrPm && targetSha ? (
              <TraceArc
                loading={modTracing}
                tooltip="Modified trace similarity"
                question={modifiedQuestion}
                modalTitle="Trace Similarity - Modified"
              />
            ) : (
              current.modstructure && modSimint !== undefined && (
                <TraceArc
                  value={modSimint}
                  tooltip="Modified trace similarity"
                  structure={current.modstructure ?? undefined}
                  modalTitle="Trace Similarity - Modified"
                />
              )
            )}
            {current.modstructure && isRbOrPm && targetSha && (
              <ScoreArc
                loading={modScoring}
                tooltip="Modified estimated score"
                question={modifiedQuestion}
                modalTitle="Estimated Score - Modified"
              />
            )}
            {/* Vertical separator between the score / trace cluster
                and the copy / download / clear cluster. */}
            {current.modstructure && (
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            )}
            {/* Copy / Download / Clear live in the top-right corner so
                the bottom footer can focus on Reduce Again / Apply /
                Cancel. Copy / Download only appear once the wizard has
                produced a modified structure. */}
            {current.modstructure && (
              <>
                <Tooltip title="Copy to Clipboard">
                  <IconButton data-testid="ww_copy_btn" size="small" onClick={handleCopy} sx={{ p: 0.25 }}>
                    <ContentCopyIcon sx={{ fontSize: ICON_XS }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download">
                  <IconButton data-testid="ww_download_btn" size="small" onClick={handleDownload} sx={{ p: 0.25 }}>
                    <DownloadIcon sx={{ fontSize: ICON_XS }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
            <Tooltip title="Clear Word Wizard">
              <IconButton data-testid="ww_clear_btn" size="small" onClick={handleClear} sx={{ p: 0.25 }}>
                <CancelOutlinedIcon sx={{ fontSize: FS_XL }} />
              </IconButton>
            </Tooltip>
          </Box>

        </Box>
      </Box>

      {/* ── View tabs ─────────────────────────────────────────────────
          A single TabHeader drives the rendering mode of *both*
          columns - flipping between Text (BasicView, free-form edit)
          and Trace (ProView, per-sentence diff with the modification
          toggles + delete affordance) on Original and Modified
          simultaneously. Only rendered once the wizard has produced a
          modified structure (the input-only paste view doesn't need
          it). */}
      {current.modstructure && !wwView.isinputview && !wwView.isprocessing && (
        <TabHeader<ViewTab>
          tabs={[{ key: 'text', label: 'Text' }, { key: 'trace', label: 'Trace' }]}
          active={viewTab}
          onChange={setViewTab}
          testIdPrefix="ww_view"
          endSlot={
            // Iteration chips ride along the right edge of the
            // Text / Trace tabs. One per iteration the user has
            // produced - clicking flicks the body to that version
            // (read-only when it isn't the latest one). Only shows
            // once the wizard has actually run.
            (iterations.length > 1 || iterations.some(it => it.modstructure)) ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ opacity: 0.55, fontSize: FS_XS, mr: 0.5 }}>
                  Iterations
                </Typography>
                {iterations.map((_, i) => (
                  <Chip
                    key={i}
                    data-testid={`ww_iter_chip_${i}`}
                    size="small"
                    label={`v${i + 1}`}
                    color={i === currentIter ? 'primary' : 'default'}
                    variant={i === currentIter ? 'filled' : 'outlined'}
                    onClick={() => setCurrentIter(i)}
                    sx={{ height: 22, fontSize: '0.7rem', cursor: 'pointer' }}
                  />
                ))}
              </Box>
            ) : undefined
          }
        />
      )}
      {/* ── Body ────────────────────────────────────────────────────── */}
      <Box
        data-testid="ww_body"
        sx={{
          flex: 1, overflowY: 'auto', p: 1,
          ...(wwView.isinputview ? {
            display: 'flex', flexDirection: 'column',
            '& > div': { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 },
            '& [data-editable]': { flex: 1, minHeight: 0 },
          } : {}),
        }}
      >

        {wwView.isinputview && (
          <TaQueryBar
            htmlContent={current.inputtext}
            onChange={html => {
              patchIteration(currentIter, { inputtext: html })
              autoAdjustLimit(html)
            }}
            editable
            placeholder="Paste your answer here to reduce the word count..."
            id="wwquerybar1"
          />
        )}

        {wwView.isprocessing && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 4, gap: 1 }}>
            <CircularProgress size={28} />
            <Typography variant="caption" sx={{ fontSize: FS_SM, opacity: 0.7 }}>
              Reducing word count...
            </Typography>
          </Box>
        )}

        {/* Side-by-side panes - two TaResponse instances over the same
            modstructure (left projected to originals only). In Trace
            view the right column carries the modification toggle +
            delete affordance per sentence; in Text view both columns
            collapse to a single contenteditable that lets the user
            rewrite freely. Editing on the right always writes back to
            `modstructure`; the left is read-only. */}
        {!wwView.isprocessing && current.modstructure && !wwView.isinputview && (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, alignItems: 'start' }}>
            <Box sx={{ minWidth: 0 }}>
              <TaResponse
                showOriginal
                editable={false}
                structure={originalStructure ?? undefined}
                onChange={handleOrigStructureNoop}
                id="ww_original_text"
              />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <TaResponse
                modificationMode
                editable
                structure={current.modstructure}
                onChange={handleModStructureChange}
                id="ww_modified_text"
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* ── Footer ──────────────────────────────────────────────────── */}
      {/* Input-view CTA - until the wizard has run, the only thing the
          user can do is press "Reduce Word Count". */}
      {wwView.isinputview && (
        <Box sx={{ flexShrink: 0, borderTop: 1, borderColor: 'divider', px: 0.75, py: 0.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ flex: 1 }} />
          <FancyButton
            testId="ww_refine_cta_btn"
            solid
            startIcon={<AutoFixHighIcon sx={{ fontSize: ICON_XS }} />}
            disabled={wwView.isprocessing || !current.inputtext.trim()}
            onClick={handleRefine}
          >
            Reduce
          </FancyButton>
        </Box>
      )}
      {/* Question mode (rb / pm referral): Reduce Again · Cancel · Apply.
          Reduce Again only enables when looking at the latest iteration -
          flicking back to an older tab is a read-only comparison. */}
      {current.modstructure && isRbOrPm && (
        <Box sx={{ flexShrink: 0, borderTop: 1, borderColor: 'divider', px: 0.75, py: 0.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ flex: 1 }} />
          <FancyButton
            testId="ww_reduce_again_btn"
            startIcon={<AutoFixHighIcon sx={{ fontSize: ICON_XS }} />}
            disabled={currentIter !== iterations.length - 1 || wwView.isprocessing}
            onClick={handleReduceAgain}
          >
            Reduce Again
          </FancyButton>
          <FancyButton testId="ww_cancel_btn" onClick={handleCancel}>Cancel</FancyButton>
          <FancyButton
            testId="ww_apply_btn"
            solid
            onClick={handleApply}
          >
            Apply
          </FancyButton>
        </Box>
      )}
      {/* Pasted-text mode - only Reduce Again in the footer (Copy /
          Download live in the top-right corner). */}
      {current.modstructure && !isRbOrPm && (
        <Box sx={{ flexShrink: 0, borderTop: 1, borderColor: 'divider', px: 0.75, py: 0.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ flex: 1 }} />
          <FancyButton
            testId="ww_reduce_again_btn"
            solid
            startIcon={<AutoFixHighIcon sx={{ fontSize: ICON_XS }} />}
            disabled={currentIter !== iterations.length - 1 || wwView.isprocessing}
            onClick={handleReduceAgain}
          >
            Reduce Again
          </FancyButton>
        </Box>
      )}

    </Box>
  )
}
